import React, { useRef, useState } from "react";
import ReactDOM from "react-dom";
import { PortalID } from "./modal/portal-types";
import { Floater } from "./ui-components/floaters/floater";
import StyledButton from "./ui-components/styled-button";
import styles from "./tableSubMenu.module.css";
import cn from "classnames";
import { CloseIcon } from "./ui-components/svg-shapes";
import { useIsCIMReorderEnabled } from "utils/url-utils";
import { useFloating, autoUpdate, offset, flip, shift, FloatingPortal, arrow } from "@floating-ui/react";
import { FontProperties } from "shared/datamodel/schemas";

interface CellStyle {
  textColor?: string;
  fontSize?: number;
  align?: "left" | "right" | "center";
  valign?: string;
  fontProps?: number;
  font?: string;
  fill?: string;
  text?: string;
}

interface StyleRule {
  row?: number;
  col?: number;
  style: CellStyle;
}

interface TableStyle {
  stroke: string;
  rules: StyleRule[];
}

export interface TableDescription {
  rows: number;
  cols: number;
  style: TableStyle;
}

function computeCellStyle(style: TableStyle, row: number, col: number) {
  let cellStyle = { backgroundColor: "#ffffff" };
  for (const rule of style.rules) {
    if ((rule.row != undefined && rule.row === row) || (rule.col != undefined && rule.col === col)) {
      cellStyle = { backgroundColor: rule.style.fill ?? "#fff" };
    }
  }
  return cellStyle;
}

const TableTemplates: TableDescription[] = [
  {
    rows: 4,
    cols: 4,
    style: {
      stroke: "#C8C8C8",
      rules: [{ row: 0, style: { fill: "#FFE7BC" } }],
    },
  },
  {
    rows: 4,
    cols: 4,
    style: {
      stroke: "#C8C8C8",
      rules: [{ row: 0, style: { fill: "#C1AAFF" } }],
    },
  },
  {
    rows: 4,
    cols: 4,
    style: {
      stroke: "#C8C8C8",
      rules: [{ col: 0, style: { fill: "#BFDCFF" } }],
    },
  },
  {
    rows: 4,
    cols: 4,
    style: {
      stroke: "#C8C8C8",
      rules: [
        { style: { align: "center" } },
        { row: 0, style: { fill: "#FFD9CD", fontSize: 16, fontProps: FontProperties.Bold } },
      ],
    },
  },
];

function Stack(props: React.PropsWithChildren<{ separator?: boolean }>) {
  const { children, separator = false } = props;
  return (
    <div className={styles.stack} data-separator={separator}>
      {children}
    </div>
  );
}

function Grid({
  rows,
  columns,
  onClick,
  defaults,
}: {
  rows: number;
  columns: number;
  onClick: (rows: number, cols: number) => void;
  defaults: { cols: number; rows: number };
}) {
  const [hoverRow, setHoverRow] = useState(defaults.rows - 1);
  const [hoverCol, setHoverCol] = useState(defaults.cols - 1);
  const message = `${hoverRow + 1} row${hoverRow == 0 ? "" : "s"}, ${hoverCol + 1} column${hoverCol == 0 ? "" : "s"}`;

  const isCimReorderEnabled = useIsCIMReorderEnabled();

  const TableView = (
    <table
      className={styles.table}
      onMouseLeave={() => {
        // when mouse leaves this element without clicking on anything, go back to default
        setHoverCol(defaults.cols - 1);
        setHoverRow(defaults.rows - 1);
      }}
    >
      <tbody>
        {Array.from({ length: rows }).map((_, i) => (
          <tr key={i}>
            {Array.from({ length: columns }).map((_, j) => (
              <td
                key={j}
                onClick={() => onClick(i + 1, j + 1)}
                onMouseEnter={() => {
                  setHoverRow(i);
                  setHoverCol(j);
                }}
                style={{
                  width: "15px",
                  height: "15px",
                  borderColor: hoverRow >= i && hoverCol >= j ? "#7AB2F4" : "#C8C8C8",
                  fill: hoverRow >= i && hoverCol >= j ? "#B8D5F980" : "white",
                }}
              />
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );

  const TableViewCIMFooterEnabled = (
    <div className={styles.tableCIMFooterEnabled}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className={styles.row}>
          {Array.from({ length: columns }).map((_, j) => (
            <div
              key={j}
              className={styles.cell}
              onClick={() => onClick(i + 1, j + 1)}
              onMouseEnter={() => {
                setHoverRow(i);
                setHoverCol(j);
              }}
              style={{
                backgroundColor: hoverRow >= i && hoverCol >= j ? "#B8D5F980" : "white",
                borderRightColor: hoverRow >= i && hoverCol >= j ? "#7AB2F4" : "#C8C8C8",
                borderBottomColor: hoverRow >= i && hoverCol >= j ? "#7AB2F4" : "#C8C8C8",
                borderTopColor: i === 0 ? (hoverRow >= i && hoverCol >= j ? "#7AB2F4" : "#C8C8C8") : undefined,
                borderLeftColor: j === 0 ? (hoverRow >= i && hoverCol >= j ? "#7AB2F4" : "#C8C8C8") : undefined,
              }}
              data-is-first-row={i === 0}
              data-is-first-col={j === 0}
              data-is-last-row={i === rows - 1}
              data-is-last-col={j === columns - 1}
            />
          ))}
        </div>
      ))}
    </div>
  );

  return (
    <Stack>
      <p>{message}</p>
      {isCimReorderEnabled ? TableViewCIMFooterEnabled : TableView}
    </Stack>
  );
}

export function TableSubMenu({
  onClick,
  relativeTo,
  currentDefault,
  onHover,
  onLeave,
}: {
  onClick: (description: TableDescription) => void;
  relativeTo: React.RefObject<HTMLElement>;
  currentDefault: any;
  onHover?: () => void;
  onLeave?: () => void;
}) {
  const portal = document.getElementById(PortalID.Floaters);
  const ref = useRef<any>(null);
  const isCimReorderEnabled = useIsCIMReorderEnabled();
  const arrowRef = useRef(null);
  const templatesArrowRef = useRef(null);

  const [templatesWindowOpen, setTemplatesWindowOpen] = useState(false);

  // Set up floating UI for main view
  const { refs: mainRefs, floatingStyles: mainFloatingStyles } = useFloating({
    open: isCimReorderEnabled && !!relativeTo.current && !templatesWindowOpen,
    placement: "top",
    middleware: [offset(15), flip(), shift({ padding: 5 }), arrow({ element: arrowRef })],
    whileElementsMounted: autoUpdate,
  });

  // Set up floating UI for templates view
  const { refs: templatesRefs, floatingStyles: templatesFloatingStyles } = useFloating({
    open: isCimReorderEnabled && !!relativeTo.current && templatesWindowOpen,
    placement: "top",
    middleware: [offset(15), flip(), shift({ padding: 5 }), arrow({ element: templatesArrowRef })],
    whileElementsMounted: autoUpdate,
  });

  // Set the reference element when it changes
  React.useEffect(() => {
    if (relativeTo.current) {
      mainRefs.setReference(relativeTo.current);
      templatesRefs.setReference(relativeTo.current);
    }
  }, [mainRefs, templatesRefs, relativeTo]);

  if (!relativeTo.current) return null;

  const Templates = TableTemplates.map((template, index) => (
    <div
      key={index}
      className={cn(styles.tableButtonContainer, styles.selectable, {
        [styles.tableButtonContainerCIMFooterEnabled]: isCimReorderEnabled,
      })}
    >
      <table className={cn(styles.table)} onClick={() => onClick(template)}>
        {Array.from({ length: template.rows }, (_, i) => (
          <tr key={i}>
            {Array.from({ length: template.cols }, (_, j) => (
              <td
                key={j}
                style={{
                  ...computeCellStyle(template.style, i, j),
                  borderColor: template.style.stroke,
                  height: "15px",
                  width: "42px",
                }}
              />
            ))}
          </tr>
        ))}
      </table>
      <button
        className={cn(styles.hiddenButton, { [styles.hiddenButtonCIMFooterEnabled]: isCimReorderEnabled })}
        onClick={() => onClick(template)}
      >
        Use template
      </button>
    </div>
  ));

  // Render content for templates view
  const renderTemplatesContent = () => (
    <Stack>
      <div>
        <div className={styles.back} onClick={() => setTemplatesWindowOpen(false)}>
          <svg xmlns="http://www.w3.org/2000/svg" width="11" height="8" fill="none">
            <path fill="#848199" d="M4 0 0 3.94v.02L3.96 8l.7-.69-2.79-2.85 9.13.01V3.5H1.87L4.7.7 4 0Z" />
          </svg>
        </div>
        <div className={styles.close} onClick={() => setTemplatesWindowOpen(false)}>
          <CloseIcon color="#848199" />
        </div>
      </div>
      <div className={styles.heading}>{isCimReorderEnabled ? "Choose a template" : "Table templates"}</div>
      <div className={styles.text}>Use tables to organize and arrange your information</div>

      {isCimReorderEnabled ? <div className={styles.templatesCIMFooterEnabled}>{Templates}</div> : Templates}
    </Stack>
  );

  // Render content for main grid view
  const renderMainContent = () => (
    <Stack separator={!isCimReorderEnabled}>
      <Grid
        rows={5}
        columns={11}
        defaults={{ cols: currentDefault.cols, rows: currentDefault.rows }}
        onClick={(rows, cols) =>
          onClick({
            rows,
            cols,
            style: {
              stroke: "#C8C8C8",
              rules: [],
            },
          })
        }
      />
      <StyledButton
        title={templatesWindowOpen ? "Hide templates" : "Show templates"}
        onClick={() => setTemplatesWindowOpen((x) => !x)}
        customStyle={{
          width: "100%",
          ...(isCimReorderEnabled ? { borderRadius: "6px", fontWeight: 400, fontSize: "12px" } : {}),
        }}
      />
    </Stack>
  );

  // When CIM footer is enabled, use FloatingPortal
  if (isCimReorderEnabled && relativeTo.current) {
    if (templatesWindowOpen) {
      return (
        <FloatingPortal id={PortalID.Floaters}>
          <div
            ref={templatesRefs.setFloating}
            style={templatesFloatingStyles}
            className={cn(styles.container, styles.templatesContainerCIMFooterEnabled)}
            onMouseEnter={onHover}
            onMouseLeave={onLeave}
          >
            {renderTemplatesContent()}
          </div>
        </FloatingPortal>
      );
    } else {
      return (
        <FloatingPortal id={PortalID.Floaters}>
          <div
            ref={mainRefs.setFloating}
            style={mainFloatingStyles}
            className={cn(styles.container, styles.containerCIMFooterEnabled)}
            onMouseEnter={onHover}
            onMouseLeave={onLeave}
          >
            {renderMainContent()}
          </div>
        </FloatingPortal>
      );
    }
  }

  // Original logic for non-CIM footer
  const element = templatesWindowOpen ? (
    <Floater
      relativeTo={relativeTo}
      side={"right"}
      className={cn(styles.container, { [styles.templatesContainerCIMFooterEnabled]: isCimReorderEnabled })}
      arrowSize={0}
    >
      <div onMouseOver={onHover} onMouseEnter={onHover} onMouseLeave={onLeave}>
        {renderTemplatesContent()}
      </div>
    </Floater>
  ) : (
    <Floater
      ref={ref}
      relativeTo={relativeTo}
      side={"right"}
      className={cn(styles.container, { [styles.containerCIMFooterEnabled]: isCimReorderEnabled })}
    >
      <div onMouseOver={onHover} onMouseEnter={onHover} onMouseLeave={onLeave}>
        {renderMainContent()}
      </div>
    </Floater>
  );

  return portal ? ReactDOM.createPortal(element, portal) : element;
}
